import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Cookies from 'universal-cookie';

import IconClose from '@components/Images/Rebranding/IconClose';
import { MarketingModalProps } from '@components/MarketingModal';
import { ModalType } from '@components/MarketingModal/Content/MarketingCopy';
import RetentionP0ContentSmall from '@components/MarketingModal/modals/Retention/P0';
import { TiModalColorScheme } from '@components/TIModal';
import useEventTracking from '@hooks/useEventTracking';
import {
  ClickTarget,
  GlobalModuleType,
  ImpressionElement,
  TrackingLocation,
  click,
  impression,
} from '@tracking';

const cookies = new Cookies();

const SHOW_NOTIFICATION_AFTER_TIMES_CLOSED_SUBSCRIBER = 1;
const SHOW_NOTIFICATION_AFTER_TIMES_CLOSED_LEAD = 2;
const COOKIE_TIME_LIMIT_SECONDS = 60 * 60 * 24;

type NotificationContentProps = Partial<MarketingModalProps> & {
  isActiveUser?: boolean;
};

const getNotificationClosedCount = (cookieName: string) => {
  const numTimesClosed = parseInt(cookies.get(cookieName));
  return isNaN(numTimesClosed) ? 0 : numTimesClosed;
};

const NotificationContent = ({
  buttonText,
  customUrl,
  id,
  isActiveUser,
  modalTheme = TiModalColorScheme.light,
  modalType,
  subtitle,
  title,
}: NotificationContentProps) => {
  const [hasClosedModal, setHasClosedModal] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { Track, trackEvent } = useEventTracking({
    creatorIds: null,
    globalModuleId: id,
    globalModuleType: GlobalModuleType.Modal,
    location: TrackingLocation.Modal,
  });

  const showAfterTimesClosed = isActiveUser
    ? SHOW_NOTIFICATION_AFTER_TIMES_CLOSED_SUBSCRIBER
    : SHOW_NOTIFICATION_AFTER_TIMES_CLOSED_LEAD;
  const cookieExpiry = isActiveUser ? null : COOKIE_TIME_LIMIT_SECONDS;
  const COOKIE_NAME = `notification-modal-closed-${id}`;

  const canShow = useCallback(() => {
    const closedCount = getNotificationClosedCount(COOKIE_NAME);
    const suppressed = closedCount >= showAfterTimesClosed;
    return !suppressed && !hasClosedModal && !isOpen;
  }, [COOKIE_NAME, showAfterTimesClosed, hasClosedModal, isOpen]);

  const closeNotification = useCallback(() => {
    const closedCount = getNotificationClosedCount(COOKIE_NAME);
    cookies.set(COOKIE_NAME, closedCount + 1, { path: '/', maxAge: cookieExpiry });
    setIsOpen(false);
    setHasClosedModal(true);
    trackEvent(click({ clickTarget: ClickTarget.Close }));
  }, [COOKIE_NAME, cookieExpiry, trackEvent]);

  // Show immediately when an event arrives and suppression rules allow.
  useEffect(() => {
    if (canShow()) {
      setIsOpen(true);
      trackEvent(impression({ impressionElement: ImpressionElement.Modal }));
    }
    // canShow's deps shift each render; we only want to evaluate once per mount,
    // since the subject pushes a fresh component tree per event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeNotification();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, closeNotification]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const renderBody = () => {
    switch (modalType) {
      case ModalType.RetentionP0:
        return (
          <RetentionP0ContentSmall
            buttonText={buttonText}
            customUrl={customUrl}
            modalTheme={modalTheme}
            subtitle={subtitle}
            title={title}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Track>
      {createPortal(
        <div
          aria-live="polite"
          className={clsx(
            'fixed bottom-24 right-16 z-[1020] w-full max-w-[calc(100vw-32px)] rounded-2 px-20 py-24 shadow-[0_0_16px_rgba(0,0,0,0.1)] xs:max-w-[364px] xs:px-32 lg:right-[calc(((100vw-78.5rem)/2)-8px)]',
            {
              'bg-grayscale-charcoal': modalTheme === TiModalColorScheme.dark,
              'bg-grayscale-beige': modalTheme === TiModalColorScheme.light,
            }
          )}
          data-test="notification-modal"
          role="status"
        >
          <button
            aria-label="Dismiss notification"
            className="absolute right-12 top-12 cursor-pointer border-none bg-transparent p-4"
            data-test="close-notification"
            onClick={closeNotification}
            type="button"
          >
            <IconClose className="text-primary-black" height={20} strokeWidth="1" width={20} />
          </button>
          {renderBody()}
        </div>,
        document.body
      )}
    </Track>
  );
};

export default NotificationContent;
